import styled from "@emotion/styled";
import { motion } from "framer-motion";
import React from "react";
import { Marker } from "react-map-gl/maplibre";
import { AddressComponents } from "../../../messages";
import TruncateText from "../../../TruncateText";
import { AssignedTaskStatus } from "../../../utils/taskUtils";

export default function AssignedTaskMapMarker(props: {
  title: string;
  addressComponents: AddressComponents | null;
  status: AssignedTaskStatus;
  isActive: boolean;
  expanded: boolean;
  onClick: React.MouseEventHandler<Element>;
}) {
  if (props.addressComponents === null) {
    return null;
  }

  const location = props.addressComponents;

  return (
    <Marker
      latitude={location.location.lat}
      longitude={location.location.lng}
      style={{
        zIndex: props.isActive ? 1 : undefined,
      }}
    >
      <DeadWhiteSpace onClick={props.onClick}>
        <Root
          data-variant={getVariant({ isActive: props.isActive, assignedTaskStatus: props.status })}
        >
          <Title>
            {props.expanded ? props.title : <TruncateText text={props.title} maxLength={6} />}
          </Title>
        </Root>
      </DeadWhiteSpace>
    </Marker>
  );
}

const DeadWhiteSpace = styled.div`
  padding: 12px;
`;

const Title = styled(motion.div)`
  font-weight: 700;
`;

function getVariant(params: { isActive: boolean; assignedTaskStatus: AssignedTaskStatus }) {
  if (params.isActive) return "active";

  switch (params.assignedTaskStatus) {
    case undefined:
      return "default";

    case "ASSIGNED":
      return "assigned";

    case "SCHEDULED":
      return "scheduled";
  }
}

const Root = styled(motion.div)<{
  ["data-variant"]: "default" | "active" | "assigned" | "scheduled";
}>`
  --bg-color: var(--tw-slate-900);
  --arrow-size: 4px;
  --ring-color: transparent;
  --gap: 8px;
  background-color: var(--bg-color);
  border-radius: 24px;
  color: var(--text-color, white);
  padding: 8px 12px;
  font-family: var(--font-family);
  font-size: 12px;
  line-height: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: var(--gap);
  box-shadow: 0 0 0 4px var(--ring-color);
  transition: all 250ms ease;
  position: relative;

  &:hover {
    cursor: pointer;
  }

  &[data-variant="assigned"] {
    --bg-color: var(--tw-amber-500);
  }

  &[data-variant="scheduled"] {
    --bg-color: var(--tw-green-500);
  }

  &[data-variant="active"] {
    --bg-color: var(--primary-color);
    --arrow-size: 8px;
    --ring-color: var(--tw-blue-200);
    font-size: 14px;
    padding: 12px 16px;
    transform: translateY(-6px);

    ${Title} {
      color: var(--primary-soft-color);
    }
  }

  &:after {
    --real-arrow-size: calc(var(--arrow-size) + 1px);
    content: "";
    position: absolute;
    bottom: calc(var(--arrow-size) * -1 + 1px);
    left: 50%;
    transform: translateX(-50%);
    width: 0;
    height: 0;
    border-left: var(--real-arrow-size) solid transparent;
    border-right: var(--real-arrow-size) solid transparent;
    border-top: var(--real-arrow-size) solid var(--bg-color);
    transition: all 250ms ease;
  }
`;
